import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
} from "@mui/material";
import { Chat as ChatIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import NavBar from "../../components/navBar/navBar";
import Loader from "../../components/Loader";
import useGetSupportConversations from "../../hooks/useGetSupportConversations";

dayjs.extend(relativeTime);

const SupportList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading } = useGetSupportConversations({ page: page + 1, pageSize });

  const conversations = data?.results ?? [];
  const total = data?.count ?? 0;

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="support" />
      <Box sx={{ pt: 2 }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell>
                  <b>Trial</b>
                </TableCell>
                <TableCell>
                  <b>SiteID-SubjectsID</b>
                </TableCell>
                <TableCell>
                  <b>Email</b>
                </TableCell>
                <TableCell>
                  <b>Status</b>
                </TableCell>
                <TableCell>
                  <b>Unread</b>
                </TableCell>
                <TableCell>
                  <b>Last Message</b>
                </TableCell>
                <TableCell>
                  <b>Time</b>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : conversations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No support conversations yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                conversations.map((conv) => (
                  <TableRow
                    key={conv.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/support/${conv.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2">{conv.trial_name || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={conv.unread_count > 0 ? "bold" : "normal"}
                      >
                        {conv.site_participant_id || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conv.patient_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={conv.is_resolved ? "Resolved" : "Open"}
                        color={conv.is_resolved ? "default" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {conv.unread_count > 0 && (
                        <Chip label={conv.unread_count} color="error" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ maxWidth: 220 }}
                      >
                        {conv.last_message_preview || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.disabled">
                        {conv.last_message_at ? dayjs(conv.last_message_at).fromNow() : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<ChatIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/support/${conv.id}`);
                        }}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Box>
    </Box>
  );
};

export default SupportList;
